"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo } from "react";
import { useMagnusClient } from "../provider";
import type { ToolUIRegistry } from "../tool-renderer";
import { artifactsProgressiveDisclosureToolRegistry, artifactsToolRegistry } from "./artifacts/registry";
import { boardsProgressiveDisclosureToolRegistry, boardsToolRegistry } from "./boards/registry";
import { chatSearchToolRegistry } from "./chat-search/registry";
import { concurProgressiveDisclosureToolRegistry, concurToolRegistry } from "./concur/registry";
import { concurTravelProgressiveDisclosureToolRegistry, concurTravelToolRegistry } from "./concur-travel/registry";
import { coreToolRegistry } from "./core/registry";
import { createToolRegistry } from "./create/registry";
import { databricksProgressiveDisclosureToolRegistry, databricksToolRegistry } from "./databricks/registry";
import { devtoolsProgressiveDisclosureToolRegistry, devtoolsToolRegistry } from "./devtools/registry";
import { documentSearchToolRegistry } from "./document-search/registry";
import { egnyteProgressiveDisclosureToolRegistry, egnyteToolRegistry } from "./egnyte/registry";
import { githubToolRegistry } from "./github/registry";
import { hostPageToolRegistry } from "./host-page/registry";
import { imageGenerationToolRegistry } from "./image-generation/registry";
import { knowledgeToolRegistry } from "./knowledge/registry";
import { knowledgeEngineToolRegistry } from "./knowledge-engine/registry";
import { magnusPlatformToolsProgressiveDisclosureToolRegistry, magnusPlatformToolsToolRegistry } from "./magnus-platform-tools/registry";
import { mailboxProgressiveDisclosureToolRegistry, mailboxToolRegistry } from "./mailbox/registry";
import { meetingLogisticsProgressiveDisclosureToolRegistry, meetingLogisticsToolRegistry } from "./meeting-logistics/registry";
import { memoryToolRegistry } from "./memory/registry";
import { mondayToolRegistry } from "./monday/registry";
import { msGraphToolRegistry } from "./ms-graph/registry";
import { multipleChoiceToolRegistry } from "./multiple-choice/registry";
import { notificationsToolRegistry } from "./notifications/registry";
import { notionToolRegistry } from "./notion/registry";
import { onenoteGraphProgressiveDisclosureToolRegistry, onenoteGraphToolRegistry } from "./onenote-graph/registry";
import { resolveToolRegistry } from "./policy";
import { procoreProgressiveDisclosureToolRegistry, procoreToolRegistry } from "./procore/registry";
import { progressiveDisclosureToolRegistry } from "./progressive-disclosure/registry";
import { quickbaseProgressiveDisclosureToolRegistry, quickbaseToolRegistry } from "./quickbase/registry";
import { sandboxToolRegistry } from "./sandbox/registry";
import { skillsToolRegistry } from "./skills-search/registry";
import { slackProgressiveDisclosureToolRegistry, slackToolRegistry } from "./slack/registry";
import { smartsheetToolRegistry } from "./smartsheet/registry";
import { weatherToolRegistry } from "./weather/registry";
import { webSearchToolRegistry } from "./web-search/registry";
import { widgetsToolRegistry } from "./widgets/registry";
import { workspaceFilesToolRegistry } from "./workspace-files/registry";
import { workspaceMemoryToolRegistry } from "./workspace-memory/registry";

/**
 * Tier 1 — Static registry. Every key here is a bare server-tool name that is
 * part of the compile-time `AllToolNames` union. Kept as its own object (and
 * exported) so a consuming app can combine it with its own static views (e.g.
 * Office) and assert exhaustive coverage of `AllToolNames` at build time —
 * see `tool-ui-exhaustiveness.ts` in `apps/web`. Adding a server tool to the
 * capabilities config without a view then becomes a compile error again.
 *
 * Views are lazy (per `bundle-dynamic-imports`) so a consumer only pays for
 * the tools that actually render.
 */
export const builtInStaticToolRegistry = {
  ...msGraphToolRegistry,
  ...artifactsToolRegistry,
  ...boardsToolRegistry,
  ...chatSearchToolRegistry,
  ...concurToolRegistry,
  ...concurTravelToolRegistry,
  ...coreToolRegistry,
  ...createToolRegistry,
  ...databricksToolRegistry,
  ...devtoolsToolRegistry,
  ...documentSearchToolRegistry,
  ...egnyteToolRegistry,
  ...hostPageToolRegistry,
  ...imageGenerationToolRegistry,
  // The legacy Knowledge Engine views, live alongside the index for the CMS
  // migration (ADR-111 §11) and deleted in the cutover commit. Its search
  // entry keys `legacyKnowledgeSearch`, so the two registries no longer
  // overlap and the spread order below carries no meaning.
  ...knowledgeEngineToolRegistry,
  ...knowledgeToolRegistry,
  ...magnusPlatformToolsToolRegistry,
  ...mailboxToolRegistry,
  ...meetingLogisticsToolRegistry,
  ...memoryToolRegistry,
  ...multipleChoiceToolRegistry,
  ...notificationsToolRegistry,
  ...onenoteGraphToolRegistry,
  ...procoreToolRegistry,
  ...quickbaseToolRegistry,
  ...sandboxToolRegistry,
  ...skillsToolRegistry,
  ...slackToolRegistry,
  ...weatherToolRegistry,
  ...webSearchToolRegistry,
  ...widgetsToolRegistry,
  ...workspaceFilesToolRegistry,
  ...workspaceMemoryToolRegistry
} satisfies ToolUIRegistry;

/**
 * Tier 2 — Dynamic registry. MCP / progressive-disclosure views whose
 * `${capabilityId}_${toolName}` keys are intentionally NOT in `AllToolNames`,
 * so they're excluded from the exhaustive static check and resolve at runtime
 * only. See ADR-057.
 */
export const builtInDynamicToolRegistry = {
  ...progressiveDisclosureToolRegistry,
  // Artifacts is a progressive-disclosure capability — its tool parts arrive
  // as `artifacts_${toolName}` (e.g. `artifacts_publishArtifact`). See ADR-057.
  ...artifactsProgressiveDisclosureToolRegistry,
  // Boards is a progressive-disclosure capability — its tool parts arrive as
  // `boards_${toolName}` (e.g. `boards_readBoard`).
  ...boardsProgressiveDisclosureToolRegistry,
  // Devtools is a progressive-disclosure capability — its tool parts arrive as
  // `devtools_${toolName}` (the bare-name entries in the static tier remain for
  // legacy persisted chats). See ADR-057 / ADR-076.
  ...devtoolsProgressiveDisclosureToolRegistry,
  // Slack is a progressive-disclosure capability — its tool parts are rewritten
  // to the namespaced `slack_${toolName}` identity, so register the Slack views
  // under those keys too (the bare-name entries in the static tier remain for
  // legacy persisted chats). See ADR-057.
  ...slackProgressiveDisclosureToolRegistry,
  // Procore is a progressive-disclosure capability — its tool parts are
  // rewritten to the namespaced `procore_${toolName}` identity, so register the
  // Procore views under those keys too (the bare-name entries in the static
  // tier remain for legacy persisted chats). See ADR-057.
  ...procoreProgressiveDisclosureToolRegistry,
  // Quickbase is a progressive-disclosure capability — its tool parts are
  // rewritten to the namespaced `quickbase_${toolName}` identity, so register
  // the Quickbase views under those keys too (the bare-name entries in the
  // static tier remain for legacy persisted chats). See ADR-057.
  ...quickbaseProgressiveDisclosureToolRegistry,
  // Databricks is a progressive-disclosure capability — its tool parts are
  // rewritten to the namespaced `databricks_${toolName}` identity, so register
  // the Databricks views under those keys too (the bare-name entries in the
  // static tier remain for legacy persisted chats). See ADR-057 / ADR-113.
  ...databricksProgressiveDisclosureToolRegistry,
  // Egnyte is a progressive-disclosure capability — its tool parts are
  // rewritten to the namespaced `egnyte_${toolName}` identity, so register the
  // Egnyte views under those keys too (the bare-name entries in the static tier
  // remain for legacy persisted chats). See ADR-057.
  ...egnyteProgressiveDisclosureToolRegistry,
  // Smartsheet is an MCP + progressive-disclosure capability — its tool parts
  // arrive as `smartsheet_${mcpToolName}`.
  ...smartsheetToolRegistry,
  // monday.com is an MCP + progressive-disclosure capability — its tool parts
  // arrive as `monday_${mcpToolName}`.
  ...mondayToolRegistry,
  // GitHub is an MCP + progressive-disclosure capability — its tool parts
  // arrive as `github_${mcpToolName}`.
  ...githubToolRegistry,
  // Notion is an MCP + progressive-disclosure capability — its tool parts
  // arrive as `notion_${mcpToolName}`.
  ...notionToolRegistry,
  // Magnus platform tools is a progressive-disclosure capability — its tool
  // parts arrive as `magnus-platform-tools_${toolName}` (e.g.
  // `magnus-platform-tools_createSkillFromSandbox`). The bare-name entries in
  // the static tier remain for any non-PD path. See ADR-057.
  ...magnusPlatformToolsProgressiveDisclosureToolRegistry,
  // Meeting logistics is a progressive-disclosure capability — its tool parts
  // are rewritten to `meeting-logistics_${toolName}`, so the slot-picker card
  // (and the booking dialog inside it) needs entries under those keys too. See
  // ADR-057 / ADR-085.
  ...meetingLogisticsProgressiveDisclosureToolRegistry,
  // The mailbox is a progressive-disclosure capability — its tool parts are
  // rewritten to `mailbox_${toolName}`, so the message-list, message, and
  // attachment cards need entries under those keys too. See ADR-057.
  ...mailboxProgressiveDisclosureToolRegistry,
  // OneNote is a progressive-disclosure capability — its tool parts are
  // rewritten to `onenote-graph_${toolName}`, so the notebook, section, page,
  // and search cards need entries under those keys too. See ADR-057.
  ...onenoteGraphProgressiveDisclosureToolRegistry,
  // Concur Travel search is a progressive-disclosure capability — its tool
  // parts arrive as `concur-travel_${toolName}`, so the flight/hotel/car
  // result cards need entries under those keys too. See ADR-057 / ADR-128.
  ...concurTravelProgressiveDisclosureToolRegistry,
  // Concur (expenses) is a progressive-disclosure capability — its tool parts
  // arrive as `concur_${toolName}`. See ADR-057.
  ...concurProgressiveDisclosureToolRegistry
} satisfies ToolUIRegistry;

/**
 * Display registry for the generic, app-agnostic built-in tools shipped by
 * the SDK (both tiers merged). Consuming apps merge this with their own
 * app-specific registries when calling `<ToolRenderer registry={...}>` (or via
 * `useToolRegistry`).
 */
export const builtInToolRegistry: ToolUIRegistry = {
  ...builtInStaticToolRegistry,
  ...builtInDynamicToolRegistry
};

/**
 * The built-in tool registry filtered by the surface's `builtInTools` policy
 * (from `<MagnusProvider builtInTools={...}>`). Consuming apps render with this
 * (merged with any app-specific registries) so each surface gets exactly the
 * Magnus tools it opted into. Defaults to the full toolset.
 */
export function useBuiltInToolRegistry() {
  const $ = _c(2);
  const {
    builtInTools
  } = useMagnusClient();
  let t0;
  if ($[0] !== builtInTools) {
    t0 = resolveToolRegistry(builtInToolRegistry, builtInTools);
    $[0] = builtInTools;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}